import { STORE_ROLES, type StoreRole } from "@graftus/schema";

import type { SidebarNavChildItem, SidebarNavFeature } from "@/config/sidebar-nav";

/**
 * The four dashboards of the platform sketch (GRA-282): `super`/`adminchef`
 * see the full view (identical except for Behörighet/Data, already gated
 * elsewhere by the access matrix), `admin` the department head's "avd" view
 * and `medarb` the employee's "mitt" view. `super`/`adminchef` may preview
 * any of the four through the "Visa som" switch; everyone else gets the one
 * their membership maps to (`effectiveStoreRole`).
 */
export type ViewAsRole = StoreRole;
export const VIEW_AS_ROLES: readonly ViewAsRole[] = STORE_ROLES;

export function isViewAsRole(value: unknown): value is ViewAsRole {
  return typeof value === "string" && (VIEW_AS_ROLES as readonly string[]).includes(value);
}

/**
 * Which nav features a role keeps, and for a tabbed feature which of its
 * sub-pages. A feature listed with `null` keeps every child; one listed with
 * a filter keeps the children whose `titleKey` or `url` matches.
 *
 * `insights` and `questions` (Insikter, Frågeställningar) used to be
 * children of `coach`, included for both roles via its `null` entry; they
 * are now their own top-level features (the unlabeled "Mer" group, GRA-280)
 * and are listed here with `null` too, so Head/Employee previews keep
 * seeing them exactly as before. `access`/`pdfReports` (Behörighet,
 * Rapporter) were never in these tables - `reports`'s filter already
 * excluded them for Head, and `reports` isn't in `EMPLOYEE_FEATURES` at
 * all - so they stay out here too, unchanged. `settings` used to pass
 * through unconditionally via `hiddenFromNav`; it's listed with `null` now
 * for the same unconditional pass-through.
 */
type ChildFilter = { titleKeys: readonly string[]; urls: readonly string[] } | null;

/** `admin` (Ledningsgrupp): the department-scoped preview, formerly "head". */
const ADMIN_FEATURES: Record<string, ChildFilter> = {
  overview: null,
  playbook: null,
  goals: null,
  // "Mål" in a sales business is the KPI page.
  performance: null,
  routines: null,
  teams: {
    titleKeys: ["salesTeam", "people", "checkIn"],
    urls: ["/coach/teams", "/people", "/coach/performance/pulse"],
  },
  coach: null,
  reports: { titleKeys: ["reports", "grossProfit"], urls: ["/reports", "/bruttovinst"] },
  insights: null,
  questions: null,
  settings: null,
};

/** `medarb` (Medarbetare): the employee's own-view preview, formerly "employee". */
const MEDARB_FEATURES: Record<string, ChildFilter> = {
  overview: null,
  routines: { titleKeys: ["today"], urls: ["/coach/routines/today"] },
  coach: null,
  insights: null,
  questions: null,
  settings: null,
};

const FEATURES_BY_ROLE: Record<
  Exclude<ViewAsRole, "super" | "adminchef">,
  Record<string, ChildFilter>
> = {
  admin: ADMIN_FEATURES,
  medarb: MEDARB_FEATURES,
};

function keepChild(child: SidebarNavChildItem, filter: ChildFilter): boolean {
  if (!filter) return true;
  return filter.titleKeys.includes(child.titleKey) || filter.urls.includes(child.url);
}

/**
 * Narrows the visible nav to what `role` sees. Pure: `super`/`adminchef`'s
 * list passes through untouched (identical except for Behörighet/Data,
 * already gated elsewhere by the access matrix); the other roles keep the
 * features in their table, with tabbed features cut down to the allowed
 * sub-pages. A tabbed feature left without a sub-page and without its own
 * url is dropped, since it would have nothing to link to.
 */
export function applyViewAs(
  features: SidebarNavFeature[],
  role: ViewAsRole,
): SidebarNavFeature[] {
  if (role === "super" || role === "adminchef") return features;
  const table = FEATURES_BY_ROLE[role];
  const out: SidebarNavFeature[] = [];
  for (const feature of features) {
    if (feature.hiddenFromNav) {
      out.push(feature);
      continue;
    }
    if (!(feature.id in table)) continue;
    const filter = table[feature.id] ?? null;
    if (!feature.children?.length || !filter) {
      out.push(feature);
      continue;
    }
    const children = feature.children.filter((c) => keepChild(c, filter));
    if (children.length === 0 && !feature.url) continue;
    out.push({ ...feature, children });
  }
  return out;
}
